import {
  createContext,
  useContext,
  useEffect,
  useLayoutEffect,
  useState,
  type ReactNode,
} from 'react';
import { Platform } from 'react-native';
import { lightTheme, darkTheme } from '@tabbio/tokens';
import type { ThemeMode } from '../lib/theme-mode';
import { useAppColorScheme } from '../hooks/useAppColorScheme';
import {
  getPersistedThemeMode,
  persistThemeMode,
} from '../lib/app-preferences-storage';

// Web-only override: softer near-black instead of pure jet black for the
// dark-mode page background. Mobile native still uses darkTheme.background
// (#000000) from @tabbio/tokens: this only affects CSS variables on web.
const WEB_DARK_BG = '#0d0d0d';

type Theme = typeof lightTheme;

interface ThemeContextValue {
  theme: Theme;
  isDark: boolean;
  themeMode: ThemeMode;
  setThemeMode: (mode: ThemeMode) => void;
}

const ThemeContext = createContext<ThemeContextValue>({
  theme: lightTheme,
  isDark: false,
  themeMode: 'system',
  setThemeMode: () => {},
});

const useThemeSyncEffect =
  typeof window !== 'undefined' ? useLayoutEffect : useEffect;

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [themeMode, setThemeModeState] = useState<ThemeMode>(getPersistedThemeMode);
  const colorScheme = useAppColorScheme(themeMode);

  const setThemeMode = (mode: ThemeMode) => {
    setThemeModeState(mode);
    persistThemeMode(mode);
  };

  const isDark = colorScheme === 'dark';
  const theme = isDark ? darkTheme : lightTheme;

  // Web: sync CSS variables + data-theme attribute for OpenAI-style theming
  useThemeSyncEffect(() => {
    if (Platform.OS !== 'web') return;
    const root = document.documentElement;
    const body = document.body;
    const appRoot = document.getElementById('root');

    const webBg = isDark ? WEB_DARK_BG : theme.background;

    root.setAttribute('data-theme', isDark ? 'dark' : 'light');
    root.style.backgroundColor = webBg;
    root.style.setProperty('color-scheme', isDark ? 'dark' : 'light');

    root.style.setProperty('--tabbio-bg', webBg);
    root.style.setProperty('--tabbio-surface', theme.surface);
    root.style.setProperty('--tabbio-surface-secondary', theme.surfaceSecondary);
    root.style.setProperty('--tabbio-surface-tertiary', theme.surfaceTertiary);
    root.style.setProperty('--tabbio-text', theme.text);
    root.style.setProperty('--tabbio-text-secondary', theme.textSecondary);
    root.style.setProperty('--tabbio-text-tertiary', theme.textTertiary);
    root.style.setProperty('--tabbio-border', theme.border);
    root.style.setProperty('--tabbio-border-subtle', theme.borderSubtle);
    root.style.setProperty('--tabbio-border-strong', theme.borderStrong);
    root.style.setProperty('--tabbio-surface-elevated', theme.surfaceElevated);
    root.style.setProperty('--tabbio-button-primary-bg', theme.buttonPrimaryBg);
    root.style.setProperty('--tabbio-button-primary-text', theme.buttonPrimaryText);
    root.style.setProperty('--tabbio-button-soft-bg', theme.buttonSoftBg);
    root.style.setProperty('--tabbio-button-soft-text', theme.buttonSoftText);
    root.style.setProperty('--tabbio-focus-ring', theme.focusRing);
    root.style.setProperty('--tabbio-success', theme.success);
    root.style.setProperty('--tabbio-success-soft', theme.successSoft);
    root.style.setProperty('--tabbio-success-border', theme.successBorder);
    root.style.setProperty('--tabbio-danger', theme.danger);
    root.style.setProperty('--tabbio-danger-soft', theme.dangerSoft);

    if (body) {
      body.style.backgroundColor = webBg;
      body.style.color = theme.text;
    }

    if (appRoot) {
      appRoot.style.backgroundColor = webBg;
      appRoot.style.color = theme.text;
    }

    // Sync the Safari URL-bar + bottom-chrome color (and PWA status bar style)
    // with the resolved theme. The bootstrap script wrote the initial tag -
    // here we keep it live when the user toggles theme at runtime.
    const removeStaticThemeColor = () => {
      const all = document.querySelectorAll('meta[name="theme-color"]');
      all.forEach((node) => {
        // Keep the live tag we own; drop any media-query fallbacks from +html.
        if (node.getAttribute('data-tabbio-theme-color') !== 'true') {
          node.parentNode?.removeChild(node);
        }
      });
    };
    removeStaticThemeColor();
    let live = document.querySelector<HTMLMetaElement>(
      'meta[name="theme-color"][data-tabbio-theme-color="true"]',
    );
    if (!live) {
      live = document.createElement('meta');
      live.setAttribute('name', 'theme-color');
      live.setAttribute('data-tabbio-theme-color', 'true');
      document.head.appendChild(live);
    }
    live.setAttribute('content', webBg);

    let status = document.querySelector<HTMLMetaElement>(
      'meta[name="apple-mobile-web-app-status-bar-style"]',
    );
    if (!status) {
      status = document.createElement('meta');
      status.setAttribute('name', 'apple-mobile-web-app-status-bar-style');
      document.head.appendChild(status);
    }
    status.setAttribute('content', isDark ? 'black-translucent' : 'default');
  }, [theme, isDark]);

  return (
    <ThemeContext.Provider value={{ theme, isDark, themeMode, setThemeMode }}>
      {children}
    </ThemeContext.Provider>
  );
}

export const useThemeContext = () => useContext(ThemeContext);
